import { getStoryblokApi, storyblokEditable } from '@storyblok/react';
import { FC, useEffect, useRef, useState } from 'react';

import ButtonArrow, {
  ButtonDirection,
} from '@/components/elements/ButtonArrow';
import Container from '@/components/elements/Container';
import Events from '@/components/elements/Features/Events';
import Project from '@/components/elements/Features/Projects';
import Spotlight from '@/components/elements/Features/Spotlights';
import ItemSlider from '@/components/elements/ItemSlider';
import Section from '@/components/elements/Section';
import StandardText, {
  StandardTextSize,
} from '@/components/elements/StandardText';

type ItemSliderProps = {
  blok: any;
  className: string;
};

const FEATURED_COMPONENTS = {
  spotlight: Spotlight,
  project: Project,
  events: Events,
};

async function getFeatured(features: string[]) {
  const storyblokApi = getStoryblokApi();
  const { data } = await storyblokApi.get(`cdn/stories`, {
    version:
      process.env.NEXT_PUBLIC_STORYBLOK_VERSION === 'published'
        ? 'published'
        : 'draft',
    by_uuids: features.join(','),
  });
  return data.stories;
}

const SliderSection: FC<ItemSliderProps> = ({ blok, className, children }) => {
  const [index, setIndex] = useState(0);
  const interval = useRef<any>();
  const container = useRef<HTMLDivElement>();
  const [width, setWidth] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);
  const [featured, setFeatured] = useState<any[]>([]);

  useEffect(() => {
    getFeatured(blok.featured).then((data) => setFeatured(data));
  }, [blok.featured]);

  function move(dir = 0) {
    setIndex(
      (prev) => (blok.featured.length + prev + dir) % blok.featured.length
    );
  }

  useEffect(() => {
    if (blok.autoAdvance) {
      interval.current = setInterval(() => move(1), blok.delay * 1000);
    }
    return () => {
      clearInterval(interval.current);
    };
  }, []);

  useEffect(() => {
    if (window.innerWidth > 640) {
      setItemWidth(width / 1.5);
    } else {
      setItemWidth(width);
    }
  }, [width]);

  useEffect(() => {
    function update() {
      if (container.current) {
        const size = container.current.getBoundingClientRect();
        setWidth(size.width);
      }
    }

    update();
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <Section
      {...storyblokEditable(blok)}
      className={`${className} overflow-hidden`}
    >
      <Container size="wider">
        <div className="grid items-center gap-8 md:grid-cols-2 md:gap-16 lg:gap-24">
          <div className="col-span-1">
            <StandardText blok={blok} size={StandardTextSize.MD} />
            {children}
          </div>
          <div>
            <div
              ref={container}
              className="h-fit w-screen overflow-hidden md:w-[50vw]"
            >
              <ItemSlider index={index} itemWidth={itemWidth} itemGap={32}>
                {featured.map((s) => {
                  const Comp = FEATURED_COMPONENTS[s.content.component];
                  return <Comp key={s.id} {...s} {...s.content} />;
                })}
              </ItemSlider>
            </div>
            <div className="mt-6 grid w-[120px] grid-cols-2 gap-4">
              <button
                className="size-[40px] cursor-pointer rounded-full border border-solid border-theme-primary p-[5px]"
                onClick={() => move(-1)}
              >
                <ButtonArrow
                  className="m-1 fill-theme-primary"
                  direction={ButtonDirection.LEFT}
                />
              </button>
              <button
                className="size-[40px] cursor-pointer rounded-full border border-solid border-theme-primary p-[5px]"
                onClick={() => move(1)}
              >
                <ButtonArrow
                  className="m-1 fill-theme-primary"
                  direction={ButtonDirection.RIGHT}
                />
              </button>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
};

export default SliderSection;
